'use client';

import { VpIcon } from '@vtmn-play/react/headless';
import classNames from 'classnames';
import { isPast } from 'date-fns';
import { useFormatter, useTranslations } from 'next-intl';
import { useMemo } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { TabsFilterValue } from '../../types/project/tabs';
import { StepSkeleton } from '../loading';
import { ProjectCard } from '../project/ProjectCard';
import { useProjectInteractionsContext } from '../provider/ProjectInteractionsProvider';
import { ProjectStep } from './ProjectStep';
import { ProjectStepsStatus } from './ProjectStepsStatus';

export const RadioButtonSVG = ({
  isPast = true,
}: {
  isPast?: boolean;
}) => {
  const variant = isPast
    ? 'var(--vp-semantic-color-border-neutral)'
    : 'var(--vp-semantic-color-border-brand)';

  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="27" height="26" fill="none">
      <circle cx="13.7" cy="13" r="12" fill="var(--vp-semantic-color-background-main)" stroke={variant} />
      <circle cx="13.7" cy="13" r="8" fill={variant} />
    </svg>
  );
};

export function ProjectSteps() {
  const t = useTranslations('PROJECT.STEPS');
  const format = useFormatter();

  const {
    collections,
    isFetchingCollection,
    project,
    fetchInteractions,
  } = useProjectInteractionsContext();

  const steps = collections[TabsFilterValue.STEPS]['hydra:member'];
  const nextPage = collections[TabsFilterValue.STEPS]['hydra:view']['hydra:next'];

  const startDate = useMemo(() => {
    return format.dateTime(
      new Date(project.start_at),
      {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
      },
    );
  }, [format, project.start_at]);

  return (
    <div data-testid="steps" className="steps centered-content">
      <div className="project-status">
        <div className="project-status__hidden-block"></div>
        <div className="project-status__block">
          <ProjectStepsStatus project={project} />
        </div>
      </div>
      {steps?.length > 0 && (
        <InfiniteScroll
          dataLength={steps?.length || 0}
          next={async () => await fetchInteractions(
            TabsFilterValue.STEPS,
            nextPage,
          )}
          hasMore={!!nextPage}
          loader={<StepSkeleton />}
          style={{ height: 'initial', overflow: 'visible' }}
        >
          <ul>
            {steps.map((interaction, index) => {
              return (
                <li
                  className="step"
                  key={`${TabsFilterValue.STEPS}-${interaction.id}`}
                >
                  <div
                    className={classNames('step__timeline', {
                      'step__timeline--last':
                        index === steps.length - 1 && !isFetchingCollection,
                    })}
                  >
                    <VpIcon name="calendar" />
                    <div className="vp-body-m">
                      {format.dateTime(
                        new Date(interaction.start_at),
                        {
                          year: 'numeric',
                          month: 'long',
                          day: '2-digit',
                        },
                      )}
                    </div>
                  </div>
                  <div className="step__timeline-icon">
                    <RadioButtonSVG
                      isPast={isPast(new Date(interaction.end_at))}
                    />
                    <div className="step__timeline-icon-background" />
                  </div>
                  <div className="step__card">
                    <div className="project-card__block">
                      <ProjectCard interaction={interaction}>
                        <ProjectStep interaction={interaction} />
                      </ProjectCard>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </InfiniteScroll>
      )}
      {steps?.length <= 0 && (
        <div className="step">
          <div className="step__timeline step__timeline--last" />
          <div className="step__timeline-icon">
            <RadioButtonSVG />
            <div className="step__timeline-icon-background" />
          </div>
          <div className="step__card">
            <div className="project-card__block">
              <div className="interactions-list__empty">
                {t('NO_INTERACTION')}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="start">
        <div className="start__block">
          <span className="vp-title-m start__title">
            {t('START_PROJECT')}
          </span>
          <p className="vp-body-s">
            {startDate}
          </p>
        </div>
      </div>
    </div>
  );
};
